import { useState } from "react";
import { Checkbox, Modal, TableRow } from "@mui/material";
import styled from "styled-components";
import CustomTable from "../../../../shared/components/CustomTable";
import { StyledTableCell } from "../../../../shared/components/CustomTable/Styled";
import TextBox from "../../../../shared/components/TextBox";
import IconButton from "../../../../shared/components/IconButton";
import DeleteIcon from "../../../../shared/svg/DeleteIcon";
import { StyledNavLink } from "../../../../shared/styled/TableStyles";
import { StyledButton } from "../../../../shared/styled/TitleBar";
import { formatInternationalNumber, parseInternationalToNumber } from "../../../../utils/format";
import { error as showError } from "../../../../utils/toast";
import { StyledBackdrop, TitleText } from "../../../MaterialOrder/single/AddMaterialModal/Styled";
import { ProductButtonRow } from "../../../ProductOrder/single/Styled";
import {
    CountBadge,
    LedgerSection,
    LedgerSectionHeader,
    LedgerTitleGroup,
    LedgerTitle,
    LedgerSubtitle,
    EmptyState,
} from "../../components";
import { useActiveMaterialsCatalog } from "../../hooks";
import {
    OutsourceCatalogMaterial,
    OutsourceSourceRow,
    SourceFirstOrderView,
    SourceFirstStandard,
    SourceFirstUpdate,
} from "../../types";
import { SourceMaterialWarning, ShortageChip } from "../SourceMaterialList/Styled";
import { materialTableContainerStyle } from "../MaterialList/Styled";
import {
    AddMaterialModalFrame,
    AddMaterialModalBody,
    ModalHeaderRow,
    ModalStepRail,
    ModalStepItem,
    ModalStepNumber,
    MaterialPickerLayout,
    MaterialPickerPanel,
    MaterialPickerList,
    MaterialPickerRowCard,
    MaterialIdentity,
    MaterialTitleLine,
    MaterialName,
    MaterialMetaLine,
    MaterialUnavailableReason,
    SelectorSearchInput,
    SelectedSummaryPanel,
    SelectorPaneHeader,
    SelectorPaneTitle,
    SelectedChipList,
    SelectedChip,
    ModalFooter,
    ReviewQuantityRow,
    ReviewField,
} from "../Styled";

const QuantityField = styled.label`
    display: block;
    width: 116px;
    span {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
    }
    input {
        font-family: "Noto Sans", sans-serif;
        font-size: 13px;
        width: 100%;
        box-sizing: border-box;
    }
`;
// Scope width overrides here because the shared cell styles impose a 140px minimum.
const SourceTable = styled.div`
    min-width: 0;
    table {
        min-width: 640px;
        table-layout: fixed;
    }
    && .MuiTableCell-root {
        min-width: 0;
        box-sizing: border-box;
    }
    && th:nth-child(1) {
        width: 110px;
    }
    && th:nth-child(2) {
        width: 145px;
    }
    && th:nth-child(3) {
        width: 58px;
    }
    && th:nth-child(4) {
        width: 135px;
    }
    && th:nth-child(5) {
        width: 105px;
    }
    && th:nth-child(6) {
        width: 48px;
    }
`;
const SourceName = styled.div`
    min-width: 130px;
    max-width: 320px;
    white-space: normal;
    overflow-wrap: break-word;
`;
export const sourceId = (standard: SourceFirstStandard) =>
    String(standard.source_material?.id || standard.source_material_id || standard.id);
export function outputEligibility(output: OutsourceCatalogMaterial, sources: OutsourceSourceRow[]) {
    const ids = new Set(sources.map((row) => String(row.id)));
    const standards = output.outsource_standards || [];
    return {
        matches: standards.some((s) => ids.has(sourceId(s))),
        missing: standards.filter((s) => !ids.has(sourceId(s))),
    };
}
const stock = (material: OutsourceCatalogMaterial) => Number(material.available_quantity ?? material.quantity ?? 0);
const standardName = (standard: SourceFirstStandard) =>
    standard.source_material?.name || standard.name || sourceId(standard);
const columns = [
    { text: "Mã vật tư", width: "112px", icon: <></>, sx: { minWidth: "112px" } },
    { text: "Tên vật tư", width: "150px", icon: <></>, sx: { minWidth: "150px" } },
    { text: "Đơn vị", width: "60px", icon: <></>, sx: { minWidth: "60px" } },
    { text: "Số lượng mang đi", width: "132px", icon: <></>, sx: { minWidth: "132px" } },
    { text: "Tồn khả dụng", width: "110px", icon: <></>, sx: { minWidth: "110px" } },
    { text: "", width: "40px", icon: <></>, sx: { minWidth: "40px" } },
];
function ShortageWarning({ rows }: { rows: OutsourceSourceRow[] }) {
    if (!rows.some((row) => row.selectedQuantity > stock(row))) return null;
    return (
        <SourceMaterialWarning role="alert">
            <span className="icon">!</span>
            <div>
                <div className="title">Không đủ vật tư nguồn để gia công</div>
                <div>Vẫn lưu và sửa được đơn. Cần nhập đủ vật tư nguồn trước khi xuất kho.</div>
            </div>
        </SourceMaterialWarning>
    );
}
interface SourceRowActions {
    isView: boolean;
    update: (id: string, quantity: number) => void;
    remove: (id: string) => void;
}
function SourceRow({ item, extraRowProps: actions }: { item: OutsourceSourceRow; extraRowProps: SourceRowActions }) {
    const shortage = item.selectedQuantity - stock(item);
    return (
        <TableRow>
            <StyledTableCell sx={{ minWidth: "112px" }}>
                <StyledNavLink to={`/materials/${item.id}`}>{item.material_code || item.id}</StyledNavLink>
            </StyledTableCell>
            <StyledTableCell sx={{ minWidth: "150px" }}>
                <SourceName>{item.name}</SourceName>
            </StyledTableCell>
            <StyledTableCell sx={{ minWidth: "60px" }}>{item.measurement_unit || "—"}</StyledTableCell>
            <StyledTableCell sx={{ minWidth: "132px" }}>
                <QuantityField>
                    <span>Số lượng mang đi {item.name}</span>
                    <TextBox
                        numeric
                        text={formatInternationalNumber(item.selectedQuantity)}
                        disabled={actions.isView}
                        onChangeText={(text) => {
                            const quantity = parseInternationalToNumber(text, showError);
                            if (Number.isFinite(quantity)) actions.update(item.id, quantity);
                        }}
                    />
                </QuantityField>
            </StyledTableCell>
            <StyledTableCell sx={{ minWidth: "110px" }}>
                <div>{formatInternationalNumber(stock(item))}</div>
                {shortage > 0 && <ShortageChip>Thiếu {formatInternationalNumber(shortage)}</ShortageChip>}
            </StyledTableCell>
            <StyledTableCell sx={{ minWidth: "40px" }}>
                {!actions.isView && (
                    <IconButton aria-label={`Xóa ${item.name}`} onClick={() => actions.remove(item.id)}>
                        <DeleteIcon />
                    </IconButton>
                )}
            </StyledTableCell>
        </TableRow>
    );
}

const outputColumns = [
    { text: "Mã vật tư", width: "112px", icon: <></> },
    { text: "Tên vật tư", width: "150px", icon: <></> },
    { text: "Đơn vị", width: "60px", icon: <></> },
    { text: "Nguồn cho 1 đơn vị nhận về", icon: <></> },
];
function OutputRow({
    item,
    extraRowProps,
}: {
    item: { id: string; material?: OutsourceCatalogMaterial };
    extraRowProps: { isLoading: boolean };
}) {
    const { id, material } = item;
    return (
        <TableRow>
            <StyledTableCell sx={{ minWidth: "112px" }}>
                <StyledNavLink to={`/materials/${id}`}>{material?.material_code || id}</StyledNavLink>
            </StyledTableCell>
            <StyledTableCell sx={{ minWidth: "150px" }}>
                {material?.name || (extraRowProps.isLoading ? "Đang tải…" : "Không tải được thông tin vật tư")}
            </StyledTableCell>
            <StyledTableCell sx={{ minWidth: "60px" }}>{material?.measurement_unit || "—"}</StyledTableCell>
            <StyledTableCell>
                {material?.outsource_standards?.map((standard) => (
                    <div key={sourceId(standard)}>
                        {standardName(standard)}: {formatInternationalNumber(standard.source_quantity_per_output || 0)}{" "}
                        {standard.source_material?.measurement_unit || standard.measurement_unit}
                    </div>
                ))}
            </StyledTableCell>
        </TableRow>
    );
}

export default function SourceFirstEditor({
    order,
    merge,
    isView,
}: {
    order: SourceFirstOrderView;
    merge: (update: SourceFirstUpdate) => void;
    isView: boolean;
}) {
    const { materials: catalog, isLoading, error, fetchMaterials } = useActiveMaterialsCatalog();
    const materials: OutsourceCatalogMaterial[] = catalog;
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(1);
    const [sources, setSources] = useState<OutsourceSourceRow[]>([]);
    const [outputs, setOutputs] = useState<string[]>([]);
    const [search, setSearch] = useState("");
    const existing = order.materials || [];
    const valid =
        sources.length > 0 && sources.every((row) => Number.isFinite(row.selectedQuantity) && row.selectedQuantity > 0);
    const candidates = materials.filter((material) => outputEligibility(material, sources).matches);
    const filterOutputs = (ids: string[], rows: OutsourceSourceRow[]) =>
        ids.filter((id) => {
            const output = materials.find((material) => String(material.id) === id);
            // Preserve saved IDs when the catalog is unavailable; never silently drop historical selections.
            return !output || outputEligibility(output, rows).missing.length === 0;
        });
    const commit = (rows: OutsourceSourceRow[], ids = (order.planned_output_ids || []).map(String)) =>
        merge({
            source_first: true,
            materials: rows.map((row) => ({
                ...row,
                provider_name: order.provider_name,
                provider_price: row.provider_price ?? 0,
            })),
            planned_output_ids: filterOutputs(ids, rows),
        });
    const start = () => {
        setSources(existing.map((row) => ({ ...row })));
        setOutputs((order.planned_output_ids || []).map(String));
        setStep(1);
        setSearch("");
        setOpen(true);
    };
    const changeSource = (id: string, quantity: number) =>
        setSources((rows) => rows.map((row) => (row.id === id ? { ...row, selectedQuantity: quantity } : row)));
    const toggleSource = (material: OutsourceCatalogMaterial, checked: boolean) =>
        setSources((rows) =>
            checked ? [...rows, { ...material, selectedQuantity: 1 }] : rows.filter((row) => row.id !== material.id),
        );
    const visibleMaterials = materials.filter((material) =>
        [material.material_code, material.name, material.measurement_unit]
            .join(" ")
            .toLowerCase()
            .includes(search.toLowerCase()),
    );
    const sourceOptions = [
        ...visibleMaterials,
        ...sources.filter(
            (row) =>
                !materials.some((material) => material.id === row.id) &&
                row.name.toLowerCase().includes(search.toLowerCase()),
        ),
    ];
    return (
        <>
            <LedgerSection>
                <LedgerSectionHeader>
                    <LedgerTitleGroup>
                        <LedgerTitle>Vật tư mang đi gia công</LedgerTitle>
                        <LedgerSubtitle>Danh sách vật tư nguồn và số lượng mang đi.</LedgerSubtitle>
                    </LedgerTitleGroup>
                    <CountBadge>{existing.length}</CountBadge>
                </LedgerSectionHeader>
                <ShortageWarning rows={existing} />
                {existing.some((row) => !Number.isFinite(row.selectedQuantity) || row.selectedQuantity <= 0) && (
                    <MaterialUnavailableReason role="alert">Số lượng mang đi phải lớn hơn 0.</MaterialUnavailableReason>
                )}
                {existing.length ? (
                    <SourceTable>
                        <CustomTable
                            itemList={existing}
                            RowElement={SourceRow}
                            TableColumns={columns}
                            containerStyle={materialTableContainerStyle}
                            extraRowProps={{
                                isView,
                                update: (id: string, quantity: number) =>
                                    commit(
                                        existing.map((row) =>
                                            row.id === id ? { ...row, selectedQuantity: quantity } : row,
                                        ),
                                    ),
                                remove: (id: string) => commit(existing.filter((row) => row.id !== id)),
                            }}
                        />
                    </SourceTable>
                ) : (
                    <EmptyState>
                        <div className="title">Chưa chọn vật tư mang đi</div>
                        <div className="body">Chọn vật tư nguồn và số lượng cần mang đi gia công.</div>
                    </EmptyState>
                )}
                {!isView && (
                    <ProductButtonRow>
                        <StyledButton variant="contained" onClick={start}>
                            Thêm vật tư
                        </StyledButton>
                    </ProductButtonRow>
                )}
            </LedgerSection>
            <LedgerSection>
                <LedgerSectionHeader>
                    <LedgerTitleGroup>
                        <LedgerTitle>Vật tư nhận về dự kiến</LedgerTitle>
                        <LedgerSubtitle>
                            Định mức khai trên vật tư nhận về, xác định lượng nguồn cần sử dụng.
                        </LedgerSubtitle>
                    </LedgerTitleGroup>
                </LedgerSectionHeader>
                {(order.planned_output_ids || []).length ? (
                    <CustomTable
                        itemList={order.planned_output_ids!.map((id) => ({
                            id,
                            material: materials.find((row) => String(row.id) === String(id)),
                        }))}
                        RowElement={OutputRow}
                        TableColumns={outputColumns}
                        containerStyle={materialTableContainerStyle}
                        extraRowProps={{ isLoading }}
                    />
                ) : (
                    <EmptyState>
                        <div className="title">Chọn sau ở đề nghị nhập kho</div>
                        <div className="body">Có thể lưu đơn chỉ với vật tư mang đi.</div>
                    </EmptyState>
                )}
            </LedgerSection>
            {open && (
                <Modal open onClose={() => setOpen(false)} BackdropComponent={StyledBackdrop}>
                    <AddMaterialModalFrame
                        $isOpen
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="source-material-title"
                        tabIndex={-1}
                    >
                        <ModalHeaderRow>
                            <TitleText id="source-material-title">Thêm vật tư gia công</TitleText>
                            <ModalStepRail aria-label="Các bước thêm vật tư">
                                <ModalStepItem $active={step === 1} $done={step === 2}>
                                    <ModalStepNumber $active={step === 1} $done={step === 2}>
                                        1
                                    </ModalStepNumber>
                                    Vật tư mang đi
                                </ModalStepItem>
                                <ModalStepItem $active={step === 2}>
                                    <ModalStepNumber $active={step === 2}>2</ModalStepNumber>Vật tư nhận về
                                </ModalStepItem>
                            </ModalStepRail>
                        </ModalHeaderRow>
                        <AddMaterialModalBody>
                            {isLoading && <LedgerSubtitle role="status">Đang tải vật tư…</LedgerSubtitle>}
                            {error && (
                                <MaterialUnavailableReason role="alert">
                                    Không tải được vật tư.{" "}
                                    <StyledButton onClick={() => fetchMaterials()}>Thử lại</StyledButton>
                                </MaterialUnavailableReason>
                            )}
                            {step === 1 ? (
                                <MaterialPickerLayout>
                                    <MaterialPickerPanel>
                                        <SelectorSearchInput
                                            aria-label="Tìm vật tư nguồn"
                                            placeholder="Tìm theo mã, tên vật tư hoặc đơn vị…"
                                            value={search}
                                            onChange={(event) => setSearch(event.target.value)}
                                        />
                                        <MaterialPickerList>
                                            {sourceOptions.map((material) => {
                                                const selected = sources.find((row) => row.id === material.id);
                                                return (
                                                    <MaterialPickerRowCard key={material.id} $selected={!!selected}>
                                                        <MaterialIdentity>
                                                            <MaterialTitleLine>
                                                                <StyledNavLink to={`/materials/${material.id}`}>
                                                                    {material.material_code || material.id}
                                                                </StyledNavLink>
                                                                <MaterialName>{material.name}</MaterialName>
                                                            </MaterialTitleLine>
                                                            <MaterialMetaLine>
                                                                <span>Đơn vị: {material.measurement_unit || "—"}</span>
                                                                <span>
                                                                    Tồn khả dụng:{" "}
                                                                    {formatInternationalNumber(stock(material))}
                                                                </span>
                                                            </MaterialMetaLine>
                                                        </MaterialIdentity>
                                                        <Checkbox
                                                            inputProps={{
                                                                "aria-label": `${material.material_code} — ${material.name}`,
                                                            }}
                                                            checked={!!selected}
                                                            onChange={(_, checked) => toggleSource(material, checked)}
                                                        />
                                                    </MaterialPickerRowCard>
                                                );
                                            })}
                                            {!isLoading && !error && !sourceOptions.length && (
                                                <EmptyState>Không tìm thấy vật tư phù hợp.</EmptyState>
                                            )}
                                        </MaterialPickerList>
                                    </MaterialPickerPanel>
                                    <SelectedSummaryPanel>
                                        <SelectorPaneHeader>
                                            <SelectorPaneTitle>Vật tư đã chọn</SelectorPaneTitle>
                                            <CountBadge>{sources.length}</CountBadge>
                                        </SelectorPaneHeader>
                                        <SelectedChipList>
                                            {sources.map((row) => (
                                                <SelectedChip key={row.id}>
                                                    <MaterialTitleLine>
                                                        <span>{row.material_code}</span>
                                                        <IconButton
                                                            aria-label={`Bỏ chọn ${row.name}`}
                                                            onClick={() => toggleSource(row, false)}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </MaterialTitleLine>
                                                    <div>{row.name}</div>
                                                    <ReviewQuantityRow>
                                                        <ReviewField>
                                                            <span>Số lượng mang đi {row.name}</span>
                                                            <TextBox
                                                                numeric
                                                                text={formatInternationalNumber(row.selectedQuantity)}
                                                                onChangeText={(text) => {
                                                                    const quantity = parseInternationalToNumber(
                                                                        text,
                                                                        showError,
                                                                    );
                                                                    if (Number.isFinite(quantity))
                                                                        changeSource(row.id, quantity);
                                                                }}
                                                            />
                                                        </ReviewField>
                                                    </ReviewQuantityRow>
                                                    <span>{row.measurement_unit}</span>
                                                </SelectedChip>
                                            ))}
                                        </SelectedChipList>
                                        {!sources.length && (
                                            <LedgerSubtitle>Chọn vật tư ở danh sách bên trái.</LedgerSubtitle>
                                        )}
                                        {sources.length > 0 && !valid && (
                                            <MaterialUnavailableReason role="alert">
                                                Số lượng mang đi phải lớn hơn 0.
                                            </MaterialUnavailableReason>
                                        )}
                                        <ShortageWarning rows={sources} />
                                    </SelectedSummaryPanel>
                                </MaterialPickerLayout>
                            ) : (
                                <MaterialPickerPanel>
                                    <LedgerSubtitle>
                                        Không bắt buộc. Định mức khai trên vật tư nhận về và liệt kê vật tư nguồn cần
                                        cho 1 đơn vị nhận về.
                                    </LedgerSubtitle>
                                    <MaterialPickerList>
                                        {candidates.map((material) => {
                                            const { missing } = outputEligibility(material, sources);
                                            return (
                                                <MaterialPickerRowCard
                                                    key={material.id}
                                                    $selected={outputs.includes(String(material.id)) && !missing.length}
                                                    $disabled={!!missing.length}
                                                >
                                                    <MaterialIdentity>
                                                        <MaterialTitleLine>
                                                            <StyledNavLink to={`/materials/${material.id}`}>
                                                                {material.material_code || material.id}
                                                            </StyledNavLink>
                                                            <MaterialName>{material.name}</MaterialName>
                                                        </MaterialTitleLine>
                                                        <MaterialMetaLine>
                                                            Đơn vị: {material.measurement_unit || "—"}
                                                        </MaterialMetaLine>
                                                        <LedgerSubtitle>
                                                            {material.outsource_standards
                                                                ?.map(
                                                                    (standard) =>
                                                                        `${standardName(standard)}: ${formatInternationalNumber(standard.source_quantity_per_output || 0)} ${standard.source_material?.measurement_unit || standard.measurement_unit || ""}`,
                                                                )
                                                                .join(" · ")}
                                                        </LedgerSubtitle>
                                                        {!!missing.length && (
                                                            <MaterialUnavailableReason>
                                                                Thiếu nguồn: {missing.map(standardName).join(", ")}
                                                            </MaterialUnavailableReason>
                                                        )}
                                                    </MaterialIdentity>
                                                    <Checkbox
                                                        inputProps={{ "aria-label": material.name }}
                                                        disabled={!!missing.length}
                                                        checked={
                                                            outputs.includes(String(material.id)) && !missing.length
                                                        }
                                                        onChange={(_, checked) =>
                                                            setOutputs((ids) =>
                                                                checked
                                                                    ? [...ids, String(material.id)]
                                                                    : ids.filter((id) => id !== String(material.id)),
                                                            )
                                                        }
                                                    />
                                                </MaterialPickerRowCard>
                                            );
                                        })}
                                    </MaterialPickerList>
                                    {!candidates.length && !isLoading && !error && (
                                        <EmptyState>
                                            Chưa có vật tư nhận về phù hợp. Bạn vẫn có thể lưu và chọn sau.
                                        </EmptyState>
                                    )}
                                </MaterialPickerPanel>
                            )}
                        </AddMaterialModalBody>
                        <ModalFooter>
                            <StyledButton variant="outlined" onClick={() => setOpen(false)}>
                                Hủy
                            </StyledButton>
                            {step === 2 && (
                                <StyledButton variant="outlined" onClick={() => setStep(1)}>
                                    Quay lại
                                </StyledButton>
                            )}
                            {step === 1 ? (
                                <StyledButton
                                    variant="contained"
                                    disabled={!valid || isLoading || !!error}
                                    onClick={() => setStep(2)}
                                >
                                    Tiếp tục
                                </StyledButton>
                            ) : (
                                <StyledButton
                                    variant="contained"
                                    disabled={!valid || isLoading || !!error}
                                    onClick={() => {
                                        commit(sources, outputs);
                                        setOpen(false);
                                    }}
                                >
                                    Lưu vật tư
                                </StyledButton>
                            )}
                        </ModalFooter>
                    </AddMaterialModalFrame>
                </Modal>
            )}
        </>
    );
}
